// src/services/api.js
import axios from 'axios';
import ConfigService from './ConfigService';

/**
 * Get the current backend URL from ConfigService
 */
const getBackendUrl = () => {
    return ConfigService.getBackendUrl();
};

/**
 * Set a new backend URL override (if allowed by server config)
 */
export const setBackendUrl = (newUrl) => {
    const success = ConfigService.setLocalBackendOverride(newUrl);
    if (success) {
        // Update API instance with new URL
        API.defaults.baseURL = `${newUrl}/api/v1`;
        console.log('Backend URL override set:', newUrl);
        return true;
    } else {
        console.warn('Failed to set backend URL override - not allowed by server configuration');
        return false;
    }
};

/**
 * Get current backend URL being used
 */
export const getCurrentBackendUrl = () => {
    return getBackendUrl();
};

/**
 * Reset backend URL to server configuration (remove local override)
 */
export const resetBackendUrl = () => {
    ConfigService.clearLocalBackendOverride();
    const serverUrl = getBackendUrl();
    API.defaults.baseURL = `${serverUrl}/api/v1`;
    console.log('Backend URL reset to server config:', serverUrl);
};

/**
 * Check if local override is active
 */
export const hasLocalBackendOverride = () => {
    return ConfigService.hasLocalOverride();
};

/**
 * Get full configuration from ConfigService
 */
export const getConfig = () => {
    return ConfigService.getConfig();
};

/**
 * Refresh configuration from server
 */
export const refreshConfig = async () => {
    const success = await ConfigService.refresh();
    if (success) {
        // Update API base URL with potentially new server config
        const newUrl = getBackendUrl();
        API.defaults.baseURL = `${newUrl}/api/v1`;
        console.log('API base URL updated after config refresh:', newUrl);
    }
    return success;
};

/**
 * Re-sync the axios instance with the current ConfigService backend URL.
 * Called by AppInitializer after config is loaded/refreshed, since this
 * module is evaluated at bundle load, before ConfigService is initialized.
 */
export const syncApiBaseUrl = () => {
    const url = getBackendUrl();
    API.defaults.baseURL = `${url}/api/v1`;
    return url;
};

// Create API instance
// Note: ConfigService should be initialized by AppInitializer before any API calls
const baseURL = getBackendUrl();
console.log('API Base URL:', baseURL);

const API = axios.create({
    baseURL: `${baseURL}/api/v1`,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
});

// Attach token to every request if available
API.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Response interceptor to handle auth errors (especially when switching backends)
API.interceptors.response.use(
    (response) => response,
    (error) => {
        // 428 = a PASSWORD_EXPIRY login policy is forcing a password change.
        // The session is still valid, so keep the token and pin the user to the
        // change-password screen instead of logging them out.
        if (error.response
            && error.response.status === 428
            && error.response.data?.code === 'PASSWORD_CHANGE_REQUIRED') {
            if (!window.location.pathname.startsWith('/change-password')) {
                if (error.response.data?.message) {
                    sessionStorage.setItem('passwordChangeReason', error.response.data.message);
                }
                window.location.href = '/change-password';
            }
            return Promise.reject(error);
        }

        if (error.response && error.response.status === 401) {
            // Handle expired token or backend mismatch
            console.warn('Authentication failed - clearing session and redirecting to login');

            // The account was deactivated mid-session. Carry the reason across
            // the hard navigation so the login screen can explain the sign-out
            // instead of the user being dumped there with no explanation.
            if (error.response.data?.code === 'ACCOUNT_DEACTIVATED' && error.response.data?.message) {
                sessionStorage.setItem('signedOutReason', error.response.data.message);
            }
            // Clear the whole session, not just the token. A leftover `user` makes
            // the app act signed-in on the login page, which re-fires the same
            // authenticated bootstrap calls and 401s again.
            localStorage.removeItem('token');
            localStorage.removeItem('user');

            // Show helpful message for backend switching
            if (ConfigService.hasLocalOverride()) {
                console.info('Note: You may need to login again when switching to a different backend');
            }

            // Already on the login screen: a hard navigation here is a full page
            // reload, so anything that 401s at login time would loop forever.
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export default API;
